
import { ForgotPasswordModal } from "@/components/ForgotPasswordModal";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useAuthContext } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

interface LoginModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRegisterClick?: () => void;
  customTitle?: string;
}

export const LoginModal = ({ open, onOpenChange, onRegisterClick, customTitle }: LoginModalProps) => {
  const [formData, setFormData] = useState({
    email: "",
    password: ""
  });
  const [loginType, setLoginType] = useState<'customer' | 'supplier'>('customer');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();

  const { signInWithEmail } = useAuthContext();

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const result = await signInWithEmail(formData.email, formData.password, loginType);
    if (!result.error) {
      onOpenChange(false);

      // Redirect suppliers to their settings page
      if (loginType === 'supplier') {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: supplierData } = await supabase
            .from('supplier_profile')
            .select('id')
            .eq('user_id', user.id)
            .maybeSingle();

          if (supplierData) {
            window.location.replace(`/supplier/${supplierData.id}`);
            return;
          }
        }
      }

      // Default behavior for customers
      window.location.reload();
    }
  };

  const handleSupplierRegisterClick = () => {
    onOpenChange(false);
    navigate('/anslut-ditt-foretag', { replace: true });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md p-0 bg-white rounded-2xl">
        <DialogClose
          className="absolute top-4 right-4 rounded-md opacity-70 ring-1 ring-black/10 hover:opacity-100 focus:outline-none transition-opacity z-10"
          aria-label="Stäng"
        >
          <X className="h-5 w-5" />
        </DialogClose>
        <div className="pt-14 px-8 pb-8">
          <DialogHeader className="text-center mb-8">
            <DialogTitle className="text-2xl font-semibold text-dark-blue">
              {customTitle || "Logga in"}
            </DialogTitle>
            <DialogDescription className="sr-only">
              Logga in för att fortsätta
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Login Type Toggle */}
            <div className="space-y-3">
              <Label className="text-sm font-medium text-gray-700">
                Jag är en
              </Label>
              <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="login-type"
                    checked={loginType === 'supplier'}
                    onCheckedChange={(checked) => setLoginType(checked ? 'supplier' : 'customer')}
                  />
                  <Label htmlFor="login-type" className="text-sm font-medium cursor-pointer">
                    {loginType === 'customer' ? 'Kund' : 'Leverantör'}
                  </Label>
                </div>
                <span className="text-xs text-gray-500">
                  {loginType === 'customer' ? 'Letar efter leverantörer' : 'Erbjuder tjänster'}
                </span>
              </div>
            </div>

            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                E-postadress
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5a8a8a] focus:ring-0"
                required
              />
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-gray-700">
                Lösenord
              </Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => handleInputChange("password", e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#5a8a8a] focus:ring-0"
                required
              />
            </div>



            {/* Login Button */}
            <Button
              type="submit"
              variant="cream-tool"
              className="w-full py-3 text-base"
            >
              Logga in som {loginType === 'customer' ? 'Kund' : 'Leverantör'}
            </Button>

            {/* Forgot Password Link */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  setShowForgotPassword(true);
                }}
                className="text-sm text-dark-blue hover:underline"
              >
                Glömt lösenord?
              </button>
            </div>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-base text-gray-600">
              Har du inte ett konto?{" "}
              <button
                type="button"
                onClick={() => {
                  onOpenChange(false);
                  onRegisterClick?.();
                }}
                className="text-[#A9C6CC] hover:underline font-medium"
              >
                Registrera dig här
              </button>
            </p>
          </div>

          {/* Supplier Register Link */}
          <div className="mt-4 text-center">
            <p className="text-base text-gray-600">
              Är du leverantör?{" "}
              <button
                type="button"
                onClick={handleSupplierRegisterClick}
                className="text-[#A9C6CC] hover:underline font-medium"
              >
                Registrera dig här
              </button>
            </p>
          </div>
        </div>
      </DialogContent>
      <ForgotPasswordModal
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
        onBackToLogin={() => {
          setShowForgotPassword(false);
          onOpenChange(true);
        }}
      />
    </Dialog>
  );
};
